import { escapeHtml } from '../ui.js'
import { apiConfigured, clientDashboard, clientLogout, clientPendingSignatures } from '../api.js'
import {
  clearClientSession,
  getClientSession,
  getClientToken,
  hasPermission,
  isClientLoggedIn,
  setClientSession,
} from '../clientAuth.js'
import {
  getCachedPortalDashboard,
  getPortalDashboardSchedule,
  shouldFetchPortalDashboard,
  storePortalDashboardSnapshot,
} from '../portalDashboardCache.js'
import {
  installationsForOrganization,
  persistPortalActiveInstallationId,
  portalDashboardHasTwin,
  resolvePortalActiveInstallationId,
  resolvePortalOrganizationId,
  syncTwinNavVisibility,
} from '../portalInstallContext.js'

function barChart(items) {
  const max = Math.max(1, ...items.map((i) => Number(i.value) || 0))
  return `
    <div class="dash-bars">
      ${items
        .map(
          (i) => `
        <div class="dash-bar-row">
          <span class="dash-bar-label">${escapeHtml(i.label)}</span>
          <div class="dash-bar-track">
            <div class="dash-bar-fill" style="width:${Math.round(((Number(i.value) || 0) / max) * 100)}%;background:${escapeHtml(i.color || '#0084ff')}"></div>
          </div>
          <span class="dash-bar-value">${Number(i.value) || 0}</span>
        </div>`
        )
        .join('')}
    </div>`
}

function wanLinesChart(items) {
  const list = Array.isArray(items) ? items : []
  if (list.length === 0) return ''
  return `
    <div class="dash-wan-lines">
      ${list
        .map((i) => {
          const up = !!i.value || i.status === 'up'
          const tone = up ? 'up' : 'down'
          const rx = i.rx_label ? `↓ ${escapeHtml(i.rx_label)}` : '↓ —'
          const tx = i.tx_label ? `↑ ${escapeHtml(i.tx_label)}` : '↑ —'
          return `
        <div class="dash-wan-line dash-wan-line--${tone}">
          <div class="dash-wan-line-main">
            <span class="dash-wan-line-name">${escapeHtml(i.label || i.iface || 'WAN')}</span>
            <span class="dash-wan-line-status">${up ? 'Online' : 'Offline'}</span>
          </div>
          <div class="dash-wan-line-traffic">
            <span>${rx}</span>
            <span>${tx}</span>
          </div>
        </div>`
        })
        .join('')}
    </div>`
}

function donut(items) {
  const total = items.reduce((s, i) => s + (Number(i.value) || 0), 0) || 1
  let acc = 0
  const stops = items.map((i) => {
    const pct = ((Number(i.value) || 0) / total) * 100
    const start = acc
    acc += pct
    return `${i.color || '#64748b'} ${start}% ${acc}%`
  })
  return `
    <div class="dash-donut-wrap">
      <div class="dash-donut" style="background:conic-gradient(${stops.join(',')})"></div>
      <ul class="dash-donut-legend">
        ${items.map((i) => `<li><span style="background:${escapeHtml(i.color || '#64748b')}"></span>${escapeHtml(i.label)}: <strong>${Number(i.value) || 0}</strong></li>`).join('')}
      </ul>
    </div>`
}

function statusBadge(status) {
  const map = {
    ok: ['Red operativa', 'dash-badge-ok'],
    warning: ['Atención', 'dash-badge-warn'],
    critical: ['Crítico', 'dash-badge-crit'],
    unknown: ['Sin datos', 'dash-badge-muted'],
  }
  const [label, cls] = map[status] || map.unknown
  return `<span class="dash-badge ${cls}">${label}</span>`
}

function meterCard(m, accent) {
  if (!m?.enabled) return ''
  return `
    <div class="dash-meter" style="--meter-accent:${accent}">
      <div class="dash-meter-head">
        <div>
          <strong>${escapeHtml(m.label || '')}</strong>
          <em>${statusBadge(m.status || 'ok')}</em>
        </div>
      </div>
      <div class="dash-meter-values">
        <div><span>Hoy</span><strong>${Number(m.today) || 0} <small>${escapeHtml(m.unit || '')}</small></strong></div>
        <div><span>Mes</span><strong>${Number(m.month) || 0} <small>${escapeHtml(m.unit || '')}</small></strong></div>
      </div>
      ${m.note ? `<p class="portal-muted dash-meter-note">${escapeHtml(m.note)}</p>` : ''}
    </div>`
}

function greetHello() {
  const h = new Date().getHours()
  if (h < 14) return 'Buenos días'
  if (h < 20) return 'Buenas tardes'
  return 'Buenas noches'
}

function greetName(session) {
  return (
    session.client?.name ||
    session.staff?.name ||
    (session.client?.email || session.staff?.email || '').split('@')[0] ||
    'Cliente'
  )
}

function formatSnapshotDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(d)
}

export function renderDashboard() {
  const session = getClientSession()
  const who = greetName(session)
  return `
    <div class="dash-app">
      <aside class="dash-side">
        <p class="dash-side-label">Portal</p>
        <nav class="dash-side-nav" aria-label="Portal cliente">
          <a href="/mi-instalacion" data-link class="is-active" aria-current="page">Resumen</a>
          <a href="/mi-instalacion/documentos" data-link id="dash-pending-nav" hidden>Documentos</a>
          <a href="/mi-instalacion/nueva-incidencia" data-link>Nueva incidencia</a>
          <a href="/mi-instalacion/presupuesto-it" data-link>Solicitar presupuesto</a>
          <a href="/mi-instalacion/gemelo" data-link id="dash-twin-btn" hidden>Gemelo 3D</a>
          <a href="/ia" data-link>Asistente IA</a>
        </nav>
        <button type="button" id="dash-logout" class="portal-btn dash-side-logout">Salir</button>
      </aside>

      <div class="dash-main">
        <div class="dash-hero dash-hero-rich">
          <div>
            <p class="portal-eyebrow">Portal cliente</p>
            <h1>${escapeHtml(greetHello())}, ${escapeHtml(who)}</h1>
            <p class="portal-muted" id="dash-subtitle">Cargando snapshot…</p>
            <div id="dash-status" class="dash-status-row"></div>
          </div>
          <div class="dash-hero-actions">
            <a href="/mi-instalacion/nueva-incidencia" data-link class="portal-btn portal-btn-primary portal-btn-rounded">Nueva incidencia</a>
            <a href="/mi-instalacion/presupuesto-it" data-link class="portal-btn portal-btn-rounded">Solicitar presupuesto</a>
          </div>
        </div>
        <div id="dash-org-select" class="portal-card dash-select-card" hidden></div>
        <div id="dash-install-select" class="portal-card dash-select-card" hidden></div>
        <div id="dash-pending-card" class="portal-card" hidden>
          <h2>Documentos pendientes</h2>
          <p class="portal-muted" id="dash-pending-lead">Contratos y mandatos SEPA a la espera de firma.</p>
          <ul id="dash-pending-list" class="dash-signals"></ul>
          <div style="margin-top:10px;">
            <a href="/mi-instalacion/documentos" data-link class="portal-btn portal-btn-primary">Ver documentos</a>
          </div>
        </div>
        <div id="dash-kpis" class="dash-kpi-grid"></div>
        <div class="dash-ops-grid">
          <div class="portal-card">
            <h2>Acciones</h2>
            <p class="portal-muted" id="dash-actions-lead">Abre un ticket o solicita un presupuesto IT con los datos de tu sesión.</p>
            <div style="display:flex;flex-wrap:wrap;gap:8px;">
              <a href="/mi-instalacion/nueva-incidencia" data-link class="portal-btn portal-btn-primary">Crear incidencia</a>
              <a href="/mi-instalacion/presupuesto-it" data-link class="portal-btn">Solicitar presupuesto</a>
            </div>
          </div>
          <div class="portal-card">
            <h2>Señales de instalación</h2>
            <ul id="dash-signals" class="dash-signals"></ul>
          </div>
        </div>
        <div id="dash-meters" class="dash-meters-grid" hidden></div>
        <div class="dash-charts-grid">
          <div class="portal-card dash-chart-card"><h2>Infraestructura</h2><div id="chart-infra"></div></div>
          <div class="portal-card dash-chart-card"><h2 id="chart-wifi-title">WiFi / DHCP</h2><div id="chart-wifi"></div></div>
          <div class="portal-card dash-chart-card"><h2>Líneas WAN</h2><div id="chart-wan"></div></div>
          <div class="portal-card dash-chart-card" id="chart-cctv-wrap"><h2>Videovigilancia</h2><div id="chart-cctv"></div></div>
          <div class="portal-card dash-chart-card" id="chart-meters-wrap" hidden><h2>Contadores</h2><div id="chart-meters"></div></div>
        </div>
        <p id="dash-msg" class="portal-muted portal-form-msg" role="status"></p>
      </div>
    </div>
  `
}

export function bindDashboard(root, { navigate }) {
  if (!isClientLoggedIn()) {
    navigate('/cliente', { replace: true })
    return
  }

  const session = getClientSession()
  const msg = root.querySelector('#dash-msg')
  const subtitle = root.querySelector('#dash-subtitle')
  let installationId = resolvePortalActiveInstallationId(session)
  let organizationId =
    resolvePortalOrganizationId(session, installationId) ||
    session.organizations?.[0]?.id ||
    session.client?.organization?.id ||
    null

  try {
    const prefInst = sessionStorage.getItem('portal_preferred_installation_id')
    if (prefInst) {
      sessionStorage.removeItem('portal_preferred_installation_id')
      const prefId = Number(prefInst)
      if (prefId && (session.installations || []).some((i) => Number(i.id) === prefId)) {
        installationId = prefId
        const inst = (session.installations || []).find((i) => Number(i.id) === prefId)
        if (inst?.organization_id) organizationId = inst.organization_id
      } else if (prefId && (session.organizations || []).length) {
        for (const org of session.organizations) {
          const hit = (org.installations || []).find((i) => Number(i.id) === prefId)
          if (hit) {
            installationId = prefId
            organizationId = org.id
            break
          }
        }
      }
    }
  } catch (_) {}

  root.querySelector('#dash-logout')?.addEventListener('click', async () => {
    try {
      await clientLogout()
    } catch (_) {}
    clearClientSession()
    navigate('/', { replace: true })
  })

  const orgs = session.organizations || []
  const orgWrap = root.querySelector('#dash-org-select')
  if (orgs.length > 1 || hasPermission('client_portal.orgs.all') || session.staff?.is_superadmin) {
    orgWrap.hidden = false
    orgWrap.innerHTML = `
      <label class="portal-label" for="organization_id">Organización</label>
      <select class="portal-input portal-select-rounded" id="organization_id">
        ${orgs
          .map(
            (o) =>
              `<option value="${o.id}" ${Number(o.id) === Number(organizationId) ? 'selected' : ''}>${escapeHtml(o.name || '')}</option>`
          )
          .join('')}
      </select>`
    orgWrap.querySelector('#organization_id')?.addEventListener('change', (ev) => {
      organizationId = Number(ev.target.value) || null
      const installs = installationsForOrganization(session, organizationId)
      installationId = installs[0]?.id || null
      persistPortalActiveInstallationId(installationId)
      syncTwinNavVisibility(root, false)
      renderInstallSelect(installs)
      clearDashboardPanels()
      load()
    })
  }

  function currentInstallList() {
    if (organizationId) {
      return installationsForOrganization(session, organizationId)
    }
    return session.installations || []
  }

  function renderInstallSelect(list) {
    const selectWrap = root.querySelector('#dash-install-select')
    if ((list || []).length > 1) {
      selectWrap.hidden = false
      selectWrap.innerHTML = `
        <label class="portal-label" for="installation_id">Instalación</label>
        <select class="portal-input portal-select-rounded" id="installation_id">
          ${(list || [])
            .map(
              (i) =>
                `<option value="${i.id}" ${Number(i.id) === Number(installationId) ? 'selected' : ''}>${escapeHtml(i.name || '')}</option>`
            )
            .join('')}
        </select>`
      selectWrap.querySelector('#installation_id')?.addEventListener('change', (ev) => {
        installationId = Number(ev.target.value) || null
        persistPortalActiveInstallationId(installationId)
        syncTwinNavVisibility(root, false)
        clearDashboardPanels()
        load()
      })
    } else {
      selectWrap.hidden = true
      selectWrap.innerHTML = ''
    }
  }

  renderInstallSelect(currentInstallList())

  let refreshTimer = null

  function clearScheduledRefresh() {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
  }

  function scheduleNextRefresh() {
    clearScheduledRefresh()
    const { nextFetchAt } = getPortalDashboardSchedule(getClientToken())
    if (!nextFetchAt) return
    const delay = Math.max(1000, new Date(nextFetchAt).getTime() - Date.now())
    refreshTimer = setTimeout(() => {
      if (document.visibilityState === 'visible') {
        load({ scheduled: true })
      } else {
        scheduleNextRefresh()
      }
    }, delay)
  }

  function clearDashboardPanels() {
    subtitle.textContent = 'Cargando snapshot…'
    root.querySelector('#dash-status').innerHTML = statusBadge('unknown')
    root.querySelector('#dash-kpis').innerHTML = ''
    root.querySelector('#dash-signals').innerHTML = ''
    root.querySelector('#dash-meters').hidden = true
    root.querySelector('#chart-meters-wrap').hidden = true
    root.querySelector('#chart-infra').innerHTML = ''
    root.querySelector('#chart-wifi').innerHTML = ''
    root.querySelector('#chart-wan').innerHTML = ''
    root.querySelector('#chart-cctv-wrap').hidden = true
    root.querySelector('#chart-cctv').innerHTML = ''
    const pendingCard = root.querySelector('#dash-pending-card')
    if (pendingCard) pendingCard.hidden = true
  }

  function renderDashboardData(data, { fromCache = false } = {}) {
    if (data.permissions) {
      setClientSession({
        ...getClientSession(),
        token: undefined,
        permissions: data.permissions,
        installations: data.installations || session.installations,
        organizations: data.organizations || session.organizations,
      })
    }
    const snap = data.snapshot || {}
    const inst = data.installation
    subtitle.textContent = inst
      ? `${inst.organization_name ? inst.organization_name + ' · ' : ''}${inst.name}${inst.city ? ' · ' + inst.city : ''}`
      : data.message || 'Sin instalación asignada'

    syncTwinNavVisibility(root, portalDashboardHasTwin(data))

    const infra = snap.infra || {}
    const wifi = snap.wifi || {}
    const wan = snap.wan || {}
    const cctv = snap.cctv || {}
    const telecom = snap.telecom || {}
    const hasTelecom = telecom.available === true
    const hasHotspot = wifi.hotspot_available === true
    const meters = snap.meters || {}
    const hasMeters =
      meters.available === true && (meters.gas?.enabled === true || meters.water?.enabled === true)

    root.querySelector('#dash-status').innerHTML = statusBadge(snap.overall_status || 'unknown')

    let kpiFourth = ''
    if ((cctv.total || 0) > 0) {
      kpiFourth = `<div class="dash-kpi"><span class="dash-kpi-label">Videovigilancia</span><strong>${cctv.online || 0}<small>/${cctv.total || 0}</small></strong><em>${escapeHtml(cctv.role || 'videovigilancia')}</em></div>`
    } else if (hasTelecom) {
      kpiFourth = `<div class="dash-kpi"><span class="dash-kpi-label">Telefonía</span><strong>${telecom.extensions_active || 0}</strong><em>${escapeHtml(telecom.status || '—')}</em></div>`
    }

    root.querySelector('#dash-kpis').innerHTML = `
        <div class="dash-kpi dash-kpi-accent"><span class="dash-kpi-label">Salud red</span><strong>${infra.health_pct || 0}<small>%</small></strong><em>${infra.online || 0}/${infra.total || 0} infra online</em></div>
        <div class="dash-kpi"><span class="dash-kpi-label">Equipos de red</span><strong>${infra.online || 0}<small>/${infra.total || 0}</small></strong><em>${infra.offline || 0} offline</em></div>
        <div class="dash-kpi"><span class="dash-kpi-label">Usuarios conectados</span><strong>${wifi.connected_devices || 0}</strong><em>Gateway · snapshot cacheado</em></div>
        ${kpiFourth}
      `

    const signals = []
    signals.push({
      label: 'Infraestructura',
      value:
        (infra.total || 0) === 0
          ? 'Sin datos'
          : `${infra.online || 0}/${infra.total || 0} online`,
      tone: (infra.offline || 0) > 0 ? 'warn' : 'ok',
    })
    if ((cctv.total || 0) > 0) {
      signals.push({
        label: 'Videovigilancia',
        value: `${cctv.online || 0}/${cctv.total || 0} cámaras online`,
        tone: (cctv.offline || 0) > 0 ? 'warn' : 'ok',
      })
    } else if (hasTelecom) {
      signals.push({
        label: 'Telefonía',
        value: `${telecom.extensions_active || 0} extensiones`,
        tone: 'muted',
      })
    }
    root.querySelector('#dash-signals').innerHTML = signals
      .map(
        (s) =>
          `<li class="dash-signal dash-signal--${s.tone}"><span>${escapeHtml(s.label)}</span><strong>${escapeHtml(s.value)}</strong></li>`
      )
      .join('')

    const lead = root.querySelector('#dash-actions-lead')
    if (lead && snap.generated_at) {
      lead.textContent = `Snapshot ${formatSnapshotDate(snap.generated_at)}. Abre un ticket con los datos de tu sesión.`
    }

    const metersEl = root.querySelector('#dash-meters')
    const canMeters = hasPermission('client_portal.meters') || session.staff?.is_superadmin
    if (canMeters && hasMeters) {
      metersEl.hidden = false
      metersEl.innerHTML = meterCard(meters.gas, '#f59e0b') + meterCard(meters.water, '#0ea5e9')
      const wrap = root.querySelector('#chart-meters-wrap')
      wrap.hidden = false
      root.querySelector('#chart-meters').innerHTML =
        (snap.charts?.meters_bars || []).length > 0
          ? barChart(snap.charts.meters_bars)
          : `<p class="portal-muted">Sin lecturas de contadores aún.</p>`
    } else {
      metersEl.hidden = true
      metersEl.innerHTML = ''
      root.querySelector('#chart-meters-wrap').hidden = true
    }

    root.querySelector('#chart-wifi-title').textContent = hasHotspot
      ? 'WiFi / Hotspot / DHCP'
      : 'WiFi / DHCP'
    const infraNote = infra.note || ''
    root.querySelector('#chart-infra').innerHTML =
      (infra.total || 0) === 0 && infraNote
        ? `<p class="portal-muted">${escapeHtml(infraNote)}</p>`
        : (infra.total || 0) === 0
          ? `<p class="portal-muted">Sin equipos de red monitorizados en esta sede.</p>`
          : donut(snap.charts?.infra_donut || [])
    root.querySelector('#chart-wifi').innerHTML =
      (snap.charts?.wifi_bars || []).length > 0
        ? barChart(snap.charts.wifi_bars)
        : `<p class="portal-muted">Sin redes WiFi publicadas en el portal. El técnico debe aprobar SSIDs en el router maestro.</p>`
    root.querySelector('#chart-wan').innerHTML =
      (snap.charts?.wan_bars || []).length > 0
        ? wanLinesChart(snap.charts.wan_bars)
        : `<p class="portal-muted">${escapeHtml(wan.note || wan.label || 'Sin líneas WAN configuradas.')}</p>`
    const cctvWrap = root.querySelector('#chart-cctv-wrap')
    if ((cctv.total || 0) > 0) {
      cctvWrap.hidden = false
      root.querySelector('#chart-cctv').innerHTML =
        (snap.charts?.cctv_bars || []).length > 0
          ? barChart(snap.charts.cctv_bars)
          : `<p class="portal-muted">${cctv.online || 0}/${cctv.total || 0} cámaras online</p>`
    } else {
      cctvWrap.hidden = true
      root.querySelector('#chart-cctv').innerHTML = ''
    }

    const schedule = getPortalDashboardSchedule(getClientToken())
    if (snap.generated_at) {
      const suffix = fromCache && schedule.nextFetchAt
        ? ` · próxima actualización ${formatSnapshotDate(schedule.nextFetchAt)}`
        : ''
      msg.textContent = `Actualizado ${formatSnapshotDate(snap.generated_at)}${suffix}`
    } else if (fromCache && schedule.nextFetchAt) {
      msg.textContent = `Próxima actualización ${formatSnapshotDate(schedule.nextFetchAt)}`
    } else {
      msg.textContent = ''
    }
  }

  function renderPendingInstallMessage() {
    syncTwinNavVisibility(root, false)
    const schedule = getPortalDashboardSchedule(getClientToken())
    subtitle.textContent = 'Seleccione instalación'
    root.querySelector('#dash-status').innerHTML = statusBadge('unknown')
    root.querySelector('#dash-kpis').innerHTML = ''
    root.querySelector('#dash-signals').innerHTML = ''
    root.querySelector('#dash-meters').hidden = true
    root.querySelector('#chart-meters-wrap').hidden = true
    root.querySelector('#chart-infra').innerHTML = ''
    root.querySelector('#chart-wifi').innerHTML = ''
    root.querySelector('#chart-wan').innerHTML = `<p class="portal-muted">Sin snapshot para esta instalación aún.</p>`
    root.querySelector('#chart-cctv-wrap').hidden = true
    const pendingCard = root.querySelector('#dash-pending-card')
    if (pendingCard) pendingCard.hidden = true
    msg.textContent = schedule.nextFetchAt
      ? `Los datos se actualizarán automáticamente el ${formatSnapshotDate(schedule.nextFetchAt)}.`
      : 'Los datos se cargarán en la próxima actualización programada.'
  }

  async function loadPendingDocs() {
    const nav = root.querySelector('#dash-pending-nav')
    const card = root.querySelector('#dash-pending-card')
    const list = root.querySelector('#dash-pending-list')
    const lead = root.querySelector('#dash-pending-lead')
    const canSee =
      hasPermission('client_portal.signing.read') ||
      hasPermission('client_portal.docs.read') ||
      session.staff?.is_superadmin
    if (!canSee) {
      if (nav) nav.hidden = true
      if (card) card.hidden = true
      return
    }
    if (nav) nav.hidden = false
    try {
      const data = await clientPendingSignatures(installationId)
      const pending = data?.pending || []
      const pendingCount = data?.pending_count ?? pending.length
      if (nav) {
        nav.textContent =
          pendingCount > 0 ? `Documentos (${pendingCount} pend.)` : 'Documentos'
      }
      if (!pending.length) {
        if (card) card.hidden = true
        return
      }
      if (card) card.hidden = false
      if (lead) {
        lead.textContent = `${pending.length} documento${pending.length === 1 ? '' : 's'} listo${pending.length === 1 ? '' : 's'} para firmar.`
      }
      if (list) {
        list.innerHTML = pending
          .slice(0, 4)
          .map((d) => {
            const url = String(d.sign_url || d.sign_path || '/mi-instalacion/documentos')
            return `<li class="dash-signal dash-signal--warn"><span>${escapeHtml(d.title || d.document_kind_label || 'Documento')}</span><a class="portal-btn portal-btn-primary" style="padding:4px 10px;font-size:.85rem;" href="${escapeHtml(url)}" rel="noopener noreferrer" target="_blank">Firmar</a></li>`
          })
          .join('')
      }
    } catch (_) {
      if (card) card.hidden = true
    }
  }

  async function load({ scheduled = false } = {}) {
    if (!apiConfigured()) {
      msg.textContent = 'Servicio no disponible.'
      syncTwinNavVisibility(root, false)
      return
    }

    if (!installationId) {
      clearDashboardPanels()
      subtitle.textContent = 'Sin sede activa para esta organización'
      msg.textContent =
        'Seleccione otra organización o asigne una instalación activa en ITManager.'
      syncTwinNavVisibility(root, false)
      return
    }

    syncTwinNavVisibility(root, false)

    const token = getClientToken()
    const shouldFetch = shouldFetchPortalDashboard(installationId, token)
    const cached = getCachedPortalDashboard(installationId, token)

    if (!shouldFetch) {
      if (cached) {
        renderDashboardData(cached, { fromCache: true })
        loadPendingDocs()
        scheduleNextRefresh()
        return
      }
      renderPendingInstallMessage()
      scheduleNextRefresh()
      return
    }

    if (!scheduled) {
      msg.textContent = 'Actualizando…'
    }

    try {
      const data = await clientDashboard(installationId)
      installationId = data.installation?.id || installationId
      persistPortalActiveInstallationId(installationId)
      storePortalDashboardSnapshot(installationId, token, data)
      renderDashboardData(data, { fromCache: false })
      loadPendingDocs()
      scheduleNextRefresh()
    } catch (e) {
      if (e.status === 401) {
        clearClientSession()
        navigate('/', { replace: true })
        return
      }
      if (cached) {
        renderDashboardData(cached, { fromCache: true })
        loadPendingDocs()
        msg.textContent = (e.message || 'Error al actualizar.') + ' Mostrando último snapshot.'
        scheduleNextRefresh()
        return
      }
      msg.textContent = e.message || 'Error al cargar el dashboard.'
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    if (shouldFetchPortalDashboard(installationId, getClientToken())) {
      load({ scheduled: true })
    } else {
      scheduleNextRefresh()
    }
  })

  load()
}
